<template>
  <!-- Template for blog posts -->
  <div class="blog-list container">
    <div class="row justify-center align-center mb-2 mb-md-4">
      <div class="col-xs-12 col-md-4 search">
        <form-input
          id="search"
          v-model="search"
          type="search"
          :label="$t(`search.label.${type}`)"
          label-visible
          label-placeholder
          debounce
          autocomplete="off"
          @input="updateSearch"
        />
      </div>
    </div>

    <div class="row-md justify-center">
      <div class="col-md-10">
        <div v-if="loading" class="articles row">
          <div
            v-for="index in 3"
            :key="index"
            class="col-xs-12 col-sm-6 col-md-4"
          >
            <article-card :article="{}" type="" skeleton />
          </div>
        </div>
        <div v-else-if="documents.length > 0" class="articles row">
          <div
            v-for="article in documents"
            :key="`${article._id}`"
            :article="article"
            class="col-xs-12 col-sm-6 col-md-4"
          >
            <article-card :article="article" :type="type" />
          </div>

          <div
            v-waypoint="{
              active: !loading && !paginateLoading,
              callback: onWaypoint,
              options: intersectionOptions,
            }"
            class="infinite"
          >
            <p v-if="!allArticlesLoaded" class="text-center">
              {{ $t('search.filter.loading') }}
            </p>
          </div>
        </div>
        <!-- If no blog posts return message -->
        <div v-else class="no-result">
          <p>{{ $t(`blog.${$route.params.type}.noPost`) }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ArticleCard from '@/components/article/Card'
import FormInput from '@/components/form/Input'

import services from '@/services'

export default {
  name: 'BlogListSlice',
  components: {
    ArticleCard,
    FormInput,
  },
  props: {
    type: { type: String, required: true },
  },
  data() {
    return {
      search: '',
      loading: true,
      paginateLoading: false,
      documents: [],
      allArticlesLoaded: false,
      intersectionOptions: {
        root: null,
        rootMargin: '250px 0px 250px 0px',
        threshold: [0, 1],
      },
      lastId: '',
      lastPublishedAt: new Date(),
    }
  },
  async fetch() {
    await this.loadArticles()
    this.loading = false
  },
  watch: {
    search() {
      this.resetState()
      this.$nextTick().then(async (_) => {
        await this.loadArticles()
      })
    },
  },
  beforeDestroy() {
    window.removeEventListener('popstate', this.onPopState, true)
  },
  created() {
    if (this.$nuxt.$route.query.search) {
      this.search = this.$nuxt.$route.query.search
    }
  },
  mounted() {
    window.addEventListener('popstate', this.onPopState, true)

    if (!this.$fetchState.pending) {
      if (this.loading) {
        if (this.documents.length === 0) {
          this.loadArticles().then((_) => {
            this.loading = false
          })
        } else {
          this.loading = false
        }
      }
    }
  },
  methods: {
    resetState() {
      this.lastId = ''
      this.lastPublishedAt = new Date()
      this.allArticlesLoaded = false
      this.documents = []
      this.loading = true
    },
    async loadArticles() {
      if (this.lastId === null || this.allArticlesLoaded) {
        return
      }

      const documents = await services.sanityService.getBlogPosts(
        {
          type: this.type,
          lang: this.$i18n.locale.slice(0, 2),
          context: this.$route.params.context,
          lastPublishedAt: this.lastPublishedAt,
          lastId: this.lastId,
        },
        this.search !== '' ? this.search : undefined
      )

      this.loading = false
      this.paginateLoading = false

      if (documents.length > 0) {
        this.lastId = documents[documents.length - 1]._id
        this.lastPublishedAt =
          documents[documents.length - 1].published_at || ''
        this.documents = [...this.documents, ...documents]

        return
      }

      this.lastId = null
      this.lastPublishedAt = null
      this.allArticlesLoaded = true
    },
    onWaypoint({ going }) {
      if (going === this.$waypointMap.GOING_IN) {
        this.paginateLoading = true
        this.loadArticles()
      }
    },
    updateSearch() {
      if (process.client) {
        const url = new URL(window.location)
        if (this.search) {
          if (
            !url.searchParams.has('search') ||
            url.searchParams.get('search') !== this.search
          ) {
            url.searchParams.set('search', this.search)
            window.history.pushState({ search: this.search }, '', url)
          }
        } else if (url.searchParams.has('search')) {
          url.searchParams.delete('search')
          window.history.pushState({}, '', url)
        }
      }
    },
    onPopState(event) {
      if (event.state && event.state.search) {
        this.search = event.state.search
      } else {
        this.search = ''
      }
    },
  },
}
</script>

<style lang="scss">
.articles {
  margin: 0 rem(-$spacing * 0.8);

  @include mq($from: tablet) {
    margin: 0 rem(-$spacing);

    .article-card {
      margin: 0 rem($spacing) rem($spacing * 3);
    }
  }

  @include mq($until: tablet) {
    .article-card {
      margin: 0 rem($spacing * 0.8) rem($spacing * 1.6);
    }
  }
}

.blog-list {
  .search {
    label {
      padding-left: var(--spacing);
    }

    input {
      padding-left: var(--spacing);
    }

    .input--label-as-placeholder.input--not-empty label,
    .input--label-as-placeholder.input--focus label {
      transform: translateY(-105%);
    }

    .input__valid {
      display: none;
    }
  }

  .no-result {
    .rich-text {
      max-width: none;
    }
  }
}
</style>
